/**
 * DocumentNumberDisplay · Mar 3, 2026 (Global Standard)
 *
 * The FINAL global helper for rendering document/reference numbers in
 * reports, registers, data tables, dashboards, planning boards, gantt
 * displays, monitoring screens and report previews.
 *
 * Splits at the LAST hyphen so any doc-number of the form
 *   `<PREFIX>-<FY>-NNNNN`  (SO / WO / CWO / PO / GRN / MRQ / MRP /
 *                           PL / DC / OPB / PRQ / PR / ESO / EPI …)
 * renders as two right-aligned lines with identical typography.
 *
 * Exported under BOTH names — `DocumentNumberDisplay` is the canonical
 * global standard the app must consume going forward; `TwoLineDocNo`
 * remains a legacy alias so the existing callsites keep working
 * without a rename storm. Both point to the same implementation.
 *
 * IMPORTANT — this is display-only. Underlying strings (search,
 * filter, sort keys, API payloads, backend links) always use the full
 * form. Add/Edit/View/Transaction screens must keep single-line.
 */
import React from "react";

function DocumentNumberDisplay({ value, className = "", testId }) {
  const raw = String(value ?? "").trim();
  if (!raw) return <span className="text-zinc-500">—</span>;
  const idx = raw.lastIndexOf("-");
  if (idx <= 0) {
    return <span data-testid={testId} className={`font-mono whitespace-nowrap ${className}`}>{raw}</span>;
  }
  const head = raw.slice(0, idx);
  const tail = raw.slice(idx);
  return (
    <span
      data-testid={testId}
      title={raw}
      className={`inline-flex flex-col items-end leading-[1.05] font-mono whitespace-nowrap ${className}`}
    >
      <span>{head}</span>
      <span>{tail}</span>
    </span>
  );
}

export { DocumentNumberDisplay };
export default DocumentNumberDisplay;
// Legacy alias — keeps every existing `import TwoLineDocNo` callsite working.
export const TwoLineDocNo = DocumentNumberDisplay;
